import { ETL_CONFIGURATION_STATUS, Values } from '../../components/configuration/types';

export const LOGIN_STATE = {
  notStarted: 'NOT_STARTED',
  initiated: 'INITIATED',
  inProgress: 'IN_PROGRESS',
  retrievingTokens: 'RETRIEVING_TOKENS',
  interrupted: 'INTERRUPTED',
  failed: 'FAILED',
  success: 'SUCCESS',
} as const;

export interface WebexNotStarted {
  loginState: typeof LOGIN_STATE.notStarted;
  intervalDelay: null;
  loginTimeout: null;
}

export interface WebexInitiated {
  loginState: typeof LOGIN_STATE.initiated;
  intervalDelay: number;
  loginTimeout: number;
  clientSecret: string;
  initialState: string;
  codeParams: ParamsType;
}

export interface WebexInProgress extends Omit<WebexInitiated, 'loginState'> {
  loginState: typeof LOGIN_STATE.inProgress;
}

export interface WebexInterrupted extends Omit<WebexNotStarted, 'loginState'> {
  loginState: typeof LOGIN_STATE.interrupted;
}

export interface WebexRetrievingTokens {
  loginState: typeof LOGIN_STATE.retrievingTokens;
  intervalDelay: null;
  loginTimeout: null;
  tokenParams: WebexTokenParamsType;
  scope: string;
}

export interface WebexSuccess {
  loginState: typeof LOGIN_STATE.success;
  intervalDelay: null;
  loginTimeout: null;
  webexTokens: WebexTokensType;
}

export interface WebexFailed {
  loginState: typeof LOGIN_STATE.failed;
  intervalDelay: null;
  loginTimeout: null;
}

export type WebexAuthorization =
  | WebexNotStarted
  | WebexInitiated
  | WebexInProgress
  | WebexInterrupted
  | WebexRetrievingTokens
  | WebexSuccess
  | WebexFailed;

export interface WebexRedirectStateType {
  code: string;
  state: string;
  loginState: Values<typeof LOGIN_STATE>;
}

export interface WebexCCRedirectMessageType {
  type: 'webexcc-oauth-redirect-state';
  payload: WebexRedirectStateType;
}

export interface ParamsType {
  response_type: string; // code
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
}

export interface WebexTokenParamsType {
  grant_type: string; // authorization_code
  client_id: string;
  client_secret: string;
  redirect_uri: string;
  code: string;
}

/**
 * @example
 *    access_token: "some random token"
 *    expires_in: 43199
 *    refresh_token: "some random refresh token"
 *    refresh_token_expires_in: 5174877
 *    token_type: "Bearer"
 *    startTime: 1665588344046 // Date.now()
 */
export interface WebexTokensType {
  access_token: string; // authorization_code
  expires_in: number;
  refresh_token: string;
  refresh_token_expires_in: number;
  token_type: string;
  startTime?: number; // timestamp when login was performed
  state: Values<typeof ETL_CONFIGURATION_STATUS>;
}

export interface WebexAuthenticationConfig {
  appUrl: string;
  paramsUrl: string;
  tokensUrl: string;
  timeoutDuration: number;
  onSuccess: () => void;
  onFailed: (cause: Values<typeof WEBEX_FAIL_REASON>) => void;
  handleTimeout: () => void;
}

export interface CacheTokensParams {
  clientId: string;
  clientSecret: string;
  scope: string;
  webexTokens: WebexTokensType;
}

export interface WebexAuthenticationAPI {
  triggerWebexLogin: (clientId: string, secret: string, scope: string) => void;
  loginState: Values<typeof LOGIN_STATE>;
  webexTokens?: WebexTokensType;
  resetAuthentication: (params?: CacheTokensParams) => void;
  wasPreviouslyLoggedIn: (clientId: string, secret: string, scope: string) => boolean;
  restorePreviousLogin: (clientId: string, secret: string, scope: string) => void;
}

export const WEBEX_FAIL_REASON = {
  tokenRequestFailed: 'TOKEN_REQUEST_FAILED',
  codeRequestFailed: 'CODE_REQUEST_FAILED',
} as const;
